import { h } from 'preact';
import { useState, useRef, useEffect } from 'preact/hooks';
import { useSuggestionsProvider } from '../search/useSearchProvider';
import styles from './BirdsnestHeaderSearch.module.scss';
import { BirdsnestProductCard } from '../components/product/BirdsnestProductCard';
import { useIsMobile } from '../utils/hooks';
import { useSwymInit } from '../utils/useSwymInit';
import { getCachedCollectionHandleForCatid, preloadCollectionHandlesFromStorefrontAPI, preloadProductInfo } from '../utils/shopify/queries';
import { trackViewSearchResults } from '../utils/elevar';
export function BirdsnestHeaderSearch() {
    useSwymInit();
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    const [hasFocused, setHasFocused] = useState(false);
    const containerRef = useRef(null);
    const closeTimerRef = useRef(null);
    const queryDebounceRef = useRef(null);
    const inputRef = useRef(null);
    const backdropTimerRef = useRef(null);
    const [categoryHandlesByCatid, setCategoryHandlesByCatid] = useState({});
    const { loading, suggestedProducts, suggestedTerms, suggestedExtras, handleSuggestionQuerySubmit } = useSuggestionsProvider('fredhopper', '', { query: 'womens' });
    const suggestedCategories = (() => {
        const group = suggestedExtras === null || suggestedExtras === void 0 ? void 0 : suggestedExtras.find((e) => String(e.indexName).includes('categories'));
        return Array.isArray(group === null || group === void 0 ? void 0 : group.suggestions) ? group.suggestions : [];
    })();
    // `suggestedCategories` is a new array each render; this stable string (sorted catids) is the useEffect
    // dependency so we only refetch collection handles when the set of categories actually changes.
    const categorySuggestionsKey = suggestedCategories.length === 0
        ? ''
        : [...suggestedCategories.map((c) => c.catid)].sort().join('\0');
    useEffect(() => {
        if (!categorySuggestionsKey) {
            setCategoryHandlesByCatid({});
            return;
        }
        let cancelled = false;
        const catids = suggestedCategories.map((c) => c.catid);
        preloadCollectionHandlesFromStorefrontAPI(catids).then(() => {
            if (cancelled)
                return;
            const next = {};
            for (const catid of catids) {
                const h = getCachedCollectionHandleForCatid(catid);
                if (h)
                    next[catid] = h;
            }
            setCategoryHandlesByCatid(next);
        });
        return () => {
            cancelled = true;
        };
    }, [categorySuggestionsKey]);
    const meta = suggestedCategories.length > 0
        ? { totalResults: suggestedCategories[0].nrResults }
        : suggestedTerms.length > 0 && typeof suggestedTerms[0].score === 'number'
            ? { totalResults: suggestedTerms[0].score }
            : null;
    // Header search used to use BirdsnestSearchPage’s useIsMobile (default 1186). utils/hooks defaults
    // to 768 (PLP/filter bar). Keep 1186 so desktop vs mobile portal targets stay unchanged.
    // Also treat coarse-pointer devices ≤1366px (iPad-class tablets) as mobile to mirror the
    // theme CSS breakpoint `@media (max-width: 1185px), (pointer: coarse) and (max-width: 1366px)`,
    // so the portal target agrees with the CSS layout. Touch-capable desktops (>1366px) keep desktop layout.
    const isMobileWidth = useIsMobile(1186);
    const [isCoarseTablet, setIsCoarseTablet] = useState(typeof window !== 'undefined'
        ? window.matchMedia('(pointer: coarse) and (max-width: 1366px)').matches
        : false);
    useEffect(() => {
        if (typeof window === 'undefined')
            return;
        const mql = window.matchMedia('(pointer: coarse) and (max-width: 1366px)');
        const handler = (e) => setIsCoarseTablet(e.matches);
        mql.addEventListener('change', handler);
        return () => mql.removeEventListener('change', handler);
    }, []);
    const isMobile = isMobileWidth || isCoarseTablet;
    useEffect(() => {
        if (!containerRef.current)
            return;
        const targetId = isMobile ? 'before-header-search-mb' : 'before-header-search';
        const targetElement = document.getElementById(targetId);
        if (targetElement && targetElement.parentNode) {
            targetElement.parentNode.insertBefore(containerRef.current, targetElement);
            // When in mobile/tablet mode (hamburger menu visible), ensure parent containers
            // aren't hidden by the Shopify theme's responsive styles.
            if (isMobile) {
                let el = containerRef.current.parentElement;
                while (el && el !== document.body) {
                    const computed = getComputedStyle(el);
                    if (computed.display === 'none')
                        el.style.display = 'block';
                    if (computed.visibility === 'hidden')
                        el.style.visibility = 'visible';
                    if (computed.opacity === '0')
                        el.style.opacity = '1';
                    el = el.parentElement;
                }
            }
        }
    }, [isMobile]);
    useEffect(() => {
        if (!hasFocused)
            return;
        handleSuggestionQuerySubmit(query || 'womens');
    }, [query, hasFocused]);
    // Batch-preload swatch product data for all suggested products
    useEffect(() => {
        var _a;
        if (loading || !suggestedProducts.length)
            return;
        const allSwatchIds = [];
        for (const product of suggestedProducts.slice(0, 5)) {
            const swatchInfo = (_a = product === null || product === void 0 ? void 0 : product.customAttributes) === null || _a === void 0 ? void 0 : _a.swatch_info;
            if (Array.isArray(swatchInfo)) {
                for (const s of swatchInfo) {
                    const id = String(s.product_id).replace(/^.*?(\d+)$/, "$1");
                    if (id)
                        allSwatchIds.push(id);
                }
            }
        }
        if (allSwatchIds.length > 0)
            preloadProductInfo(allSwatchIds);
    }, [loading, suggestedProducts]);
    // Dedupe by suggestion content, not array reference, since two effects can refetch the same default query on mount.
    const trackedSuggestionKey = useRef(null);
    useEffect(() => {
        if (!open || loading || suggestedProducts.length === 0)
            return;
        const key = suggestedProducts.slice(0, 5).map((p) => p.id).join(',');
        if (trackedSuggestionKey.current === key)
            return;
        trackedSuggestionKey.current = key;
        trackViewSearchResults(suggestedProducts.slice(0, 5), 'fh_quick_search', query || 'womens');
    }, [open, loading, suggestedProducts, query]);
    return (h("div", { class: styles.headerSearch, ref: containerRef, onMouseLeave: () => {
            if (closeTimerRef.current)
                clearTimeout(closeTimerRef.current);
            closeTimerRef.current = setTimeout(() => setOpen(false), 400);
        }, onMouseEnter: () => {
            if (closeTimerRef.current) {
                clearTimeout(closeTimerRef.current);
                closeTimerRef.current = null;
            }
            if (backdropTimerRef.current) {
                clearTimeout(backdropTimerRef.current);
                backdropTimerRef.current = null;
            }
        } },
        h("div", { class: styles.searchInputContainer },
            h("button", { onClick: () => {
                    var _a, _b;
                    const val = ((_b = (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.value) === null || _b === void 0 ? void 0 : _b.trim()) || query.trim();
                    window.location.href = `/search?q=${encodeURIComponent(val)}`;
                }, type: "button", class: styles.searchIcon, "aria-label": "Search" },
                h("svg", { width: "12", height: "12", viewBox: "0 0 12 12", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
                    h("path", { d: "M8.22188 8.22188L10.5 10.5M5.4375 9.375C7.61212 9.375 9.375 7.61212 9.375 5.4375C9.375 3.26288 7.61212 1.5 5.4375 1.5C3.26288 1.5 1.5 3.26288 1.5 5.4375C1.5 7.61212 3.26288 9.375 5.4375 9.375Z", stroke: "#2C2B2B", "stroke-width": "1.35417", "stroke-linecap": "round", "stroke-linejoin": "round" }))),
            h("input", { type: "search", ref: inputRef, class: styles.searchInput, placeholder: "What are you looking for?", onInput: (e) => {
                    const value = e.currentTarget.value;
                    if (!open)
                        setOpen(true);
                    if (queryDebounceRef.current)
                        clearTimeout(queryDebounceRef.current);
                    queryDebounceRef.current = setTimeout(() => setQuery(value), 300);
                }, onFocus: () => {
                    var _a, _b;
                    setOpen(true);
                    setHasFocused(true);
                    const value = (_b = (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : '';
                    if (value) {
                        setQuery(value);
                    }
                }, onKeyDown: (e) => {
                    var _a, _b;
                    if (e.key === 'Enter') {
                        const val = (_b = (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.value) === null || _b === void 0 ? void 0 : _b.trim();
                        if (val) {
                            e.preventDefault();
                            window.location.href = `/search?q=${encodeURIComponent(val)}`;
                        }
                    }
                } }),
            query && (h("span", { class: styles.clearIcon, onClick: (e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setQuery('');
                    if (inputRef.current)
                        inputRef.current.value = '';
                    setOpen(true);
                }, "aria-label": "Clear search" },
                h("svg", { width: "10", height: "10", viewBox: "0 0 10 10", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
                    h("path", { d: "M9.91306 9.4864L5.42356 5.01511L9.88293 0.543807C10.0035 0.422961 10.0035 0.241692 9.88293 0.120846C9.7624 0 9.58162 0 9.4611 0.120846L5.00173 4.59214L0.542356 0.0906344C0.421832 -0.0302115 0.241047 -0.0302115 0.120524 0.0906344C0 0.21148 0 0.392749 0.120524 0.513595L4.57989 5.01511L0.0903926 9.4864C-0.0301309 9.60725 -0.0301309 9.78852 0.0903926 9.90937C0.210916 10.0302 0.391701 10.0302 0.512225 9.90937L5.00173 5.43807L9.4611 9.90937C9.58162 10.0302 9.7624 10.0302 9.88293 9.90937C10.0336 9.78852 10.0336 9.63746 9.91306 9.4864Z", fill: "#2C2B2B" }))))),
        open && (h("div", { class: styles.backdrop, onClick: () => {
                if (closeTimerRef.current)
                    clearTimeout(closeTimerRef.current);
                closeTimerRef.current = setTimeout(() => setOpen(false), 400);
                setQuery('');
                // if (inputRef.current) inputRef.current.value = '';
            }, onMouseEnter: () => {
                if (backdropTimerRef.current)
                    clearTimeout(backdropTimerRef.current);
                backdropTimerRef.current = setTimeout(() => {
                    setOpen(false);
                    setQuery('');
                    // if (inputRef.current) inputRef.current.value = '';
                }, 400);
            } })),
        open && (h("div", { class: styles.dropdown },
            h("div", { class: styles.leftPane },
                suggestedTerms.length > 0 && (h("div", null,
                    h("h3", { class: styles.suggestTitle }, "suggestions"),
                    h("ul", { class: styles.suggestList }, suggestedTerms.map((item) => (h("li", { key: item.term },
                        h("a", { href: `/search?q=${encodeURIComponent(item.term)}`, title: item.term }, item.term))))))),
                suggestedCategories.length > 0 && (h("div", null,
                    h("h3", { class: styles.suggestTitle }, "categories"),
                    h("ul", { class: styles.suggestList }, suggestedCategories.map((cat) => {
                        const collectionHandle = categoryHandlesByCatid[cat.catid];
                        const href = collectionHandle
                            ? `/collections/${encodeURIComponent(collectionHandle)}`
                            : `#`;
                        return (h("li", { key: cat.catid },
                            h("a", { href: href, title: cat.mlValue }, cat.mlValue)));
                    })))),
                loading && isMobile && h("div", { class: `${styles.loading} ${styles.mb24}` }, "Loading\u2026"),
                !loading && isMobile && !suggestedProducts.length && query && (h("div", { class: `${styles.empty} ${styles.mb24}` },
                    "No results for \"",
                    query,
                    "\"")),
                query && Boolean(meta === null || meta === void 0 ? void 0 : meta.totalResults) && !loading && h("a", { href: `/search?q=${encodeURIComponent(query.trim())}`, class: styles.seeAll },
                    "view ",
                    (meta === null || meta === void 0 ? void 0 : meta.totalResults) || 0,
                    " results for \"",
                    query,
                    "\"")),
            !isMobile && h("div", { class: styles.rightPane },
                loading && h("div", { class: styles.loading }, "Loading\u2026"),
                !loading && !suggestedProducts.length && query && (h("div", { class: styles.empty },
                    "No results for \"",
                    query,
                    "\"")),
                !loading && !!suggestedProducts.length && (h("div", { class: styles.productList }, suggestedProducts.slice(0, 5).map((product, index) => (h("div", { key: product.id, class: styles.productItem },
                    h(BirdsnestProductCard, { product: product, layout: "gridFour", isModelView: true, smallCard: true, isHeaderSearch: true, trackingListId: "fh_quick_search", trackingPosition: index })))))))))));
}
